/**
 * MODULE: _shared/lib/frontend/api/accountApi.ts
 * PURPOSE: Sales Accounts API client - CRUD for account hierarchy, contacts, value pyramid
 *
 * DEPENDENCIES:
 *  - axios: HTTP client
 *  - ./config: API_CONFIG, getAuthHeaders
 *
 * PUBLIC API:
 *  - Account, Contact, AccountHierarchy, CreateAccountRequest, CreateContactInput, UpdateContactInput, UpdateAccountValuePyramidInput
 *  - accountApi.list, get, create, getHierarchy, getContacts, promoteFromSalesIntelligence, listSalesIntelligenceCompanies
 *  - accountApi.getSubsidiaries, linkSubsidiary, unlinkSubsidiary, updateValuePyramid
 *  - accountApi.createContact, updateContact, deleteContact, setPrimaryContact
 *
 * SIDE EFFECTS:
 *  - HTTP requests to /api/v1/sales-accounts endpoints
 *  - Reads JWT token from localStorage
 *
 * AGENT SAFETY: RESTRICTED
 * RECENT: 2026-07-07 [2.2.1521] - Contact + UpdateContactInput gain stakeholder-risk fields (buying_role, relationship_strength, sentiment, last_engaged_at) for the risk-aware org chart.
 * RECENT_PRIOR: 2026-02-25 - Added hierarchy, contacts CRUD, updateValuePyramid for UI migration from salesAccountsApi
 */

import axios from 'axios';
import { API_CONFIG, getAuthHeaders } from './config';

const API_BASE = API_CONFIG.baseURL;
const SALES_ACCOUNTS = `${API_BASE}/api/v1/sales-accounts`;

export interface Account {
  id: string;
  tenant_id: string;
  name: string;
  industry?: string;
  website?: string;
  revenue?: number;
  employee_count?: number;
  status: string;
  parent_account_id?: string;
  source: 'crm' | 'sales_intelligence';
  created_at: string;
  updated_at: string;
  /** From list response */
  account_hierarchy_level?: number;
  is_parent_account?: boolean;
  subsidiary_count?: number;
  score?: number | null;
  temperature?: 'hot' | 'warm' | 'cold' | null;
  value_pyramid_goals?: string | null;
  value_pyramid_strategies?: string | null;
  value_pyramid_initiatives?: string | null;
  value_pyramid_obstacles?: string | null;
  value_pyramid_needs?: string | null;
  sales_intelligence_company_id?: string | null;
  /** CRM hub territory assignment (from crm_hub_accounts.territory_id) */
  territory_id?: string | null;
  /** Linked public.accounts row when mapped from CRM hub (sales_account_id bridge). */
  sales_account_id?: string | null;
}

/** Align Team risk-aware stakeholder map: buyer graph as a risk signal. */
export type BuyingRole =
  | 'champion'
  | 'decision_maker'
  | 'influencer'
  | 'blocker'
  | 'detractor'
  | 'neutral';
export type RelationshipStrength = 'strong' | 'moderate' | 'weak';
export type StakeholderSentiment = 'positive' | 'neutral' | 'negative';

export interface Contact {
  id: string;
  account_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  job_title?: string | null;
  department?: string | null;
  company?: string | null;
  is_primary_contact: boolean;
  linkedin_url?: string | null;
  notes?: string | null;
  reports_to?: string | null;
  buying_role?: BuyingRole | null;
  relationship_strength?: RelationshipStrength | null;
  sentiment?: StakeholderSentiment | null;
  last_engaged_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateAccountRequest {
  name: string;
  industry?: string;
  website?: string;
  revenue?: number;
  employee_count?: number;
  status?: string;
  territory_id?: string;
  parent_account_id?: string;
}

export interface CreateContactInput {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  department?: string;
  company?: string;
  is_primary_contact?: boolean;
  linkedin_url?: string;
  notes?: string;
}

export type UpdateContactInput = Omit<Partial<CreateContactInput>, 'job_title' | 'company'> & {
  /** Forms may send null to clear title */
  job_title?: string | null;
  company?: string | null;
  reports_to?: string | null;
  buying_role?: BuyingRole | null;
  relationship_strength?: RelationshipStrength | null;
  sentiment?: StakeholderSentiment | null;
  last_engaged_at?: string | null;
};

export interface UpdateAccountValuePyramidInput {
  goals?: string;
  strategies?: string;
  initiatives?: string;
  obstacles?: string;
  needs?: string;
}

export interface AccountHierarchy {
  account: Account;
  ancestors: Account[];
  descendants: Account[];
}

export interface AccountListResponse {
  accounts: Account[];
  total: number;
  page?: number;
  per_page?: number;
}

export interface ContactListResponse {
  contacts: Contact[];
  total: number;
  page?: number;
  per_page?: number;
}

export interface SubsidiariesResponse {
  subsidiaries: Account[];
  total: number;
  page?: number;
  per_page?: number;
}

export const accountApi = {
  list: async (params?: {
    territory_id?: string;
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
    include_sales_intelligence?: boolean;
    source_filter?: 'crm' | 'sales_intelligence' | 'all';
  }): Promise<AccountListResponse> => {
    const response = await axios.get(`${SALES_ACCOUNTS}/accounts`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  get: async (accountId: string): Promise<Account> => {
    const response = await axios.get(
      `${SALES_ACCOUNTS}/accounts/${accountId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  create: async (account: CreateAccountRequest): Promise<Account> => {
    const response = await axios.post(
      `${SALES_ACCOUNTS}/accounts`,
      account,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  getHierarchy: async (accountId: string): Promise<AccountHierarchy> => {
    const response = await axios.get(
      `${SALES_ACCOUNTS}/accounts/${accountId}/hierarchy`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  getSubsidiaries: async (
    accountId: string,
    page = 1,
    perPage = 20
  ): Promise<SubsidiariesResponse> => {
    const response = await axios.get(
      `${SALES_ACCOUNTS}/accounts/${accountId}/subsidiaries`,
      { params: { page, per_page: perPage }, headers: getAuthHeaders() }
    );
    return response.data;
  },

  linkSubsidiary: async (
    parentId: string,
    subsidiaryId: string
  ): Promise<Account> => {
    const response = await axios.post(
      `${SALES_ACCOUNTS}/accounts/${parentId}/subsidiaries`,
      { subsidiary_id: subsidiaryId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  unlinkSubsidiary: async (
    parentId: string,
    subsidiaryId: string
  ): Promise<Account> => {
    const response = await axios.delete(
      `${SALES_ACCOUNTS}/accounts/${parentId}/subsidiaries/${subsidiaryId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  updateValuePyramid: async (
    accountId: string,
    data: UpdateAccountValuePyramidInput
  ): Promise<Account> => {
    const response = await axios.patch(
      `${SALES_ACCOUNTS}/accounts/${accountId}/value-pyramid`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  getContacts: async (
    accountId: string,
    params?: { page?: number; per_page?: number; search?: string }
  ): Promise<ContactListResponse> => {
    const response = await axios.get(
      `${SALES_ACCOUNTS}/accounts/${accountId}/contacts`,
      { params, headers: getAuthHeaders() }
    );
    return response.data;
  },

  createContact: async (
    accountId: string,
    data: CreateContactInput
  ): Promise<Contact> => {
    const response = await axios.post(
      `${SALES_ACCOUNTS}/accounts/${accountId}/contacts`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  updateContact: async (
    contactId: string,
    data: UpdateContactInput
  ): Promise<Contact> => {
    const response = await axios.patch(
      `${SALES_ACCOUNTS}/contacts/${contactId}`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  deleteContact: async (contactId: string): Promise<void> => {
    await axios.delete(`${SALES_ACCOUNTS}/contacts/${contactId}`, {
      headers: getAuthHeaders(),
    });
  },

  setPrimaryContact: async (contactId: string): Promise<Contact> => {
    const response = await axios.patch(
      `${SALES_ACCOUNTS}/contacts/${contactId}/set-primary`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  promoteFromSalesIntelligence: async (
    companyId: string,
    parentAccountId?: string
  ): Promise<Account> => {
    const response = await axios.post(
      `${SALES_ACCOUNTS}/accounts/promote-from-sales-intelligence`,
      { company_id: companyId, parent_account_id: parentAccountId ?? null },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  listSalesIntelligenceCompanies: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    not_promoted?: boolean;
  }): Promise<{ companies: unknown[]; total: number; page?: number; per_page?: number }> => {
    const response = await axios.get(
      `${SALES_ACCOUNTS}/accounts/sales-intelligence/companies`,
      { params, headers: getAuthHeaders() }
    );
    return response.data;
  },
};
